import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Pencil, Trash2, Shield, RotateCcw, Lock } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Definicja wszystkich statusów z ich właściwościami
const STATUS_CONFIG = {
  'lead': { label: '1. Lead/do pomiaru', color: '#0000FF' },
  'akceptacja': { label: '2. Akceptacja', color: '#FFFF00' },
  'brak_akceptacji': { label: '3. Brak akceptacji', color: '#696969' },
  'oplacone': { label: '4. Opłacone', color: '#2E8B57' },
  'dostawa': { label: '5. Dostawa', color: '#800080' },
  'w_realizacji': { label: '6. W realizacji', color: '#FFA500' },
  'zakonczone': { label: '7. Zakończono', color: '#006400' }
};

// Domyślne uprawnienia statusów dla ról
const DEFAULT_STATUS_PERMISSIONS = {
  'admin': Object.keys(STATUS_CONFIG),
  'user': ['lead', 'akceptacja', 'brak_akceptacji', 'oplacone', 'dostawa', 'w_realizacji'],
  'guest': [],
  'montaz': ['dostawa', 'w_realizacji', 'zakonczone'], // Montażysta: tylko statusy 5, 6, 7
  'asystent_biura': ['lead', 'akceptacja', 'brak_akceptacji', 'oplacone', 'dostawa', 'w_realizacji']
};

export const RolesManager = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    color: '#64748b',
    permissions: [],
    allowedStatuses: [] // Uprawnienia do statusów
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, permissionsRes] = await Promise.all([
        axios.get(`${API_URL}/roles`),
        axios.get(`${API_URL}/permissions`)
      ]);
      setRoles(rolesRes.data.filter((r) => r.id !== 'montaz'));
      setPermissions(permissionsRes.data);
    } catch (error) {
      toast.error('Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {});

  const openCreateDialog = () => {
    setEditingRole(null);
    setFormData({
      id: '',
      name: '',
      color: '#64748b',
      permissions: [],
      allowedStatuses: []
    });
    setDialogOpen(true);
  };

  const openEditDialog = (role) => {
    // Pobierz domyślne uprawnienia statusów dla tej roli
    const defaultStatusPerms = DEFAULT_STATUS_PERMISSIONS[role.id] || [];
    
    setEditingRole(role);
    setFormData({
      id: role.id,
      name: role.name,
      color: role.color,
      permissions: [...role.permissions],
      allowedStatuses: role.allowedStatuses || defaultStatusPerms
    });
    setDialogOpen(true);
  };

  const handleStatusPermissionToggle = (statusId) => {
    setFormData(prev => ({
      ...prev,
      allowedStatuses: prev.allowedStatuses.includes(statusId)
        ? prev.allowedStatuses.filter(s => s !== statusId)
        : [...prev.allowedStatuses, statusId]
    }));
  };

  const handlePermissionToggle = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const handleSelectAllCategory = (category) => {
    const categoryPermIds = groupedPermissions[category].map(p => p.id);
    const allSelected = categoryPermIds.every(id => formData.permissions.includes(id));
    
    if (allSelected) {
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !categoryPermIds.includes(p))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...categoryPermIds])]
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Podaj nazwę roli');
      return;
    }

    try {
      if (editingRole) {
        await axios.patch(`${API_URL}/roles/${editingRole.id}`, {
          name: editingRole.is_system ? undefined : formData.name,
          color: editingRole.is_system ? undefined : formData.color,
          permissions: formData.permissions
        });
        toast.success('Rola zaktualizowana');
      } else {
        if (!formData.id) {
          toast.error('Podaj identyfikator roli');
          return;
        }
        await axios.post(`${API_URL}/roles`, formData);
        toast.success('Rola utworzona');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd zapisywania roli');
    }
  };

  const handleDelete = async (roleId) => {
    try {
      await axios.delete(`${API_URL}/roles/${roleId}`);
      toast.success('Rola usunięta');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd usuwania roli');
    }
  };

  const handleResetToDefault = async (roleId) => {
    try {
      await axios.delete(`${API_URL}/roles/${roleId}`);
      toast.success('Rola przywrócona do ustawień domyślnych');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd przywracania roli');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-[var(--crm-text-secondary)]">Zarządzaj rolami i uprawnieniami użytkowników</p>
        <Button onClick={openCreateDialog} className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Nowa Rola
        </Button>
      </div>

      <div className="grid gap-4">
        {roles.map((role) => (
          <Card key={role.id} className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: role.color + '20' }}
                >
                  <Shield className="w-5 h-5" style={{ color: role.color }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-[var(--crm-text-primary)] font-semibold">{role.name}</h3>
                    <Badge 
                      className="text-white text-xs"
                      style={{ backgroundColor: role.color }}
                    >
                      {role.id}
                    </Badge>
                    {role.is_system && (
                      <Badge className="bg-slate-600 text-white text-xs">Systemowa</Badge>
                    )}
                  </div>
                  <p className="text-[var(--crm-text-secondary)] text-sm mt-1">
                    {role.permissions.length} uprawnień
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => openEditDialog(role)}
                  className="bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-card-hover)] text-white"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edytuj
                </Button>
                
                {role.is_system ? (
                  role.updated_at && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Przywróć
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Przywrócić domyślne?</AlertDialogTitle>
                          <AlertDialogDescription className="text-[var(--crm-text-secondary)]">
                            Przywrócisz domyślne uprawnienia dla roli "{role.name}".
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-[var(--crm-bg-btn-secondary)] text-white border-0">Anuluj</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleResetToDefault(role.id)}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            Przywróć
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Usunąć rolę?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[var(--crm-text-secondary)]">
                          Czy na pewno chcesz usunąć rolę "{role.name}"? 
                          Tej operacji nie można cofnąć.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-[var(--crm-bg-btn-secondary)] text-white border-0">Anuluj</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(role.id)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Usuń
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>

            {/* Permissions preview */}
            <div className="mt-4 flex flex-wrap gap-1">
              {role.permissions.slice(0, 8).map(permId => {
                const perm = permissions.find(p => p.id === permId);
                return perm ? (
                  <Badge key={permId} className="bg-[var(--crm-bg-panel)] text-[var(--crm-text-secondary)] text-xs">
                    {perm.name}
                  </Badge>
                ) : null;
              })}
              {role.permissions.length > 8 && (
                <Badge className="bg-[var(--crm-bg-panel)] text-[var(--crm-text-secondary)] text-xs">
                  +{role.permissions.length - 8} więcej
                </Badge>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingRole ? `Edytuj rolę: ${editingRole.name}` : 'Nowa rola'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-4">
              {!editingRole && (
                <div>
                  <Label className="text-[var(--crm-text-secondary)]">Identyfikator *</Label>
                  <Input
                    value={formData.id}
                    onChange={(e) => setFormData({...formData, id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                    placeholder="np. asystent_biura"
                    className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-white"
                  />
                  <p className="text-[var(--crm-text-muted)] text-xs mt-1">Używany w systemie, bez spacji</p>
                </div>
              )}
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Nazwa wyświetlana *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="np. Asystent biura"
                  disabled={editingRole?.is_system}
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-white disabled:opacity-50"
                />
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Kolor</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    disabled={editingRole?.is_system}
                    className="w-12 h-10 p-1 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] cursor-pointer disabled:opacity-50"
                  />
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    disabled={editingRole?.is_system}
                    className="flex-1 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-white disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div>
              <Label className="text-[var(--crm-text-secondary)] text-lg mb-4 block">Uprawnienia</Label>
              <div className="space-y-4">
                {Object.entries(groupedPermissions).map(([category, perms]) => {
                  const allSelected = perms.every(p => formData.permissions.includes(p.id));
                  const someSelected = perms.some(p => formData.permissions.includes(p.id));
                  
                  return (
                    <div key={category} className="bg-[var(--crm-bg-panel)] rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={() => handleSelectAllCategory(category)}
                          className="border-[var(--crm-border)] data-[state=checked]:bg-[#FF6B35]"
                        />
                        <span className="text-white font-medium">{category}</span>
                        {someSelected && !allSelected && (
                          <span className="text-[var(--crm-text-secondary)] text-xs">(częściowo)</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 ml-6">
                        {perms.map(perm => (
                          <div key={perm.id} className="flex items-center gap-2">
                            <Checkbox
                              checked={formData.permissions.includes(perm.id)}
                              onCheckedChange={() => handlePermissionToggle(perm.id)}
                              className="border-[var(--crm-border)] data-[state=checked]:bg-[#FF6B35]"
                            />
                            <span className="text-[var(--crm-text-secondary)] text-sm">{perm.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Status Permissions - RBAC dla statusów zleceń */}
            <div>
              <Label className="text-[var(--crm-text-secondary)] text-lg mb-4 block flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#FF6B35]" />
                Uprawnienia do zmiany statusów zleceń
              </Label>
              <p className="text-[var(--crm-text-muted)] text-sm mb-4">
                Określ które statusy może zmieniać użytkownik z tą rolą. 
                {editingRole?.id === 'montaz' && (
                  <span className="text-amber-400 ml-2">
                    (Montażysta widzi tylko statusy 5-7 na tablicy Kanban)
                  </span>
                )}
              </p>
              <div className="bg-[var(--crm-bg-panel)] rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(STATUS_CONFIG).map(([statusId, statusInfo]) => {
                    const isChecked = formData.allowedStatuses.includes(statusId);
                    const isMontazRestricted = editingRole?.id === 'montaz' && 
                      !['dostawa', 'w_realizacji', 'zakonczone'].includes(statusId);
                    
                    return (
                      <div 
                        key={statusId} 
                        className={`flex items-center gap-3 p-2 rounded-lg ${isMontazRestricted ? 'opacity-40' : 'hover:bg-[var(--crm-bg-main)]'}`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => handleStatusPermissionToggle(statusId)}
                          disabled={isMontazRestricted}
                          className="border-[var(--crm-border)] data-[state=checked]:bg-[#FF6B35]"
                        />
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: statusInfo.color }}
                        />
                        <span className={`text-sm ${isChecked ? 'text-white' : 'text-[var(--crm-text-secondary)]'}`}>
                          {statusInfo.label}
                        </span>
                        {isMontazRestricted && (
                          <Lock className="w-3 h-3 text-[var(--crm-text-muted)] ml-auto" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={() => setDialogOpen(false)} 
              variant="outline" 
              className="border-[var(--crm-border)] text-[var(--crm-text-secondary)]"
            >
              Anuluj
            </Button>
            <Button 
              onClick={handleSubmit} 
              className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white"
            >
              {editingRole ? 'Zapisz zmiany' : 'Utwórz rolę'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
